/**
 * NotificationPreferences - Wave 5 Group-C (2026-05-27).
 *
 * Per-type × per-channel preference matrix backed by
 * GET/PUT /v1/notifications/preferences. Lets the operator decide
 * which notification types should pop in-app, send email, push to
 * Web Push, or play a sound. Quiet-hours and global toggles round
 * out the page.
 */
import { Rx, useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  Bell,
  Mail,
  Smartphone,
  Volume2,
  Moon,
  RefreshCw,
  Save,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { EmptyStateV2 } from '@/components/ui/empty-state-v2';
import { useWebPushOptIn } from '@foctta/ui-shell';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreferences,
  type ChannelPrefs,
} from '@/lib/api';
import { cn } from '@/lib/cn';

const NOTIFICATION_TYPES: Array<{ key: string; label: string; group: string }> = [
  // Findings
  { key: 'finding.critical', label: 'Finding · Critical', group: 'Findings' },
  { key: 'finding.high', label: 'Finding · High', group: 'Findings' },
  // Detection
  { key: 'decision.blocked', label: 'Decision blocked', group: 'Detection' },
  { key: 'playbook.executed', label: 'Playbook executed', group: 'Detection' },
  { key: 'rule.proposed', label: 'Rule proposed', group: 'Detection' },
  // Breaches
  { key: 'breach.opened', label: 'Breach opened', group: 'Breaches' },
  { key: 'breach.clock.warning', label: 'Breach clock warning', group: 'Breaches' },
  { key: 'breach.clock.expired', label: 'Breach clock expired', group: 'Breaches' },
  // DSAR + Consent
  { key: 'dsar.due_soon', label: 'DSAR due soon', group: 'Subject rights' },
  { key: 'dsar.overdue', label: 'DSAR overdue', group: 'Subject rights' },
  { key: 'consent.revoked', label: 'Consent revoked', group: 'Subject rights' },
  // Compliance + Connectors
  { key: 'evidence.ready', label: 'Evidence pack ready', group: 'Compliance' },
  { key: 'connector.failing', label: 'Connector failing', group: 'Connectors' },
  { key: 'connector.unhealthy', label: 'Connector unhealthy', group: 'Connectors' },
  { key: 'scan.completed', label: 'Scan completed', group: 'Connectors' },
  // System
  { key: 'system.maintenance', label: 'System maintenance', group: 'System' },
  { key: 'system.update', label: 'System update', group: 'System' },
  { key: 'bridge.invitation', label: 'Cross-product invitation', group: 'System' },
];

const DEFAULT_PREFS: ChannelPrefs = { in_app: true, sound: false, push: false, email: false };

const CHANNELS: Array<{ key: keyof ChannelPrefs; label: string; icon: typeof Bell }> = [
  { key: 'in_app', label: 'In-app', icon: Bell },
  { key: 'sound', label: 'Sound', icon: Volume2 },
  { key: 'push', label: 'Push', icon: Smartphone },
  { key: 'email', label: 'Email', icon: Mail },
];

export function NotificationPreferencesPage(): JSX.Element {
  const tx = useRegulationText();
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Wave 6 (2026-05-27) - Web Push opt-in. The hook talks to
  // /v1/notifications/{vapid-public-key, preferences} directly; we
  // only need to mirror its state in our local prefs so the Web Push
  // toggle below stays in sync without a refresh.
  const webPush = useWebPushOptIn({});

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const result = await getNotificationPreferences();
      setPrefs(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  function getChannel(type: string, ch: keyof ChannelPrefs): boolean {
    const channels = prefs?.channels ?? {};
    return channels[type]?.[ch] ?? DEFAULT_PREFS[ch];
  }

  function toggle(type: string, ch: keyof ChannelPrefs): void {
    setPrefs((prev) => {
      if (!prev) return prev;
      const channels = { ...(prev.channels ?? {}) };
      const existing = channels[type] ?? { ...DEFAULT_PREFS };
      channels[type] = { ...existing, [ch]: !existing[ch] };
      return { ...prev, channels };
    });
  }

  function setColumnAll(ch: keyof ChannelPrefs, value: boolean): void {
    setPrefs((prev) => {
      if (!prev) return prev;
      const channels = { ...(prev.channels ?? {}) };
      for (const t of NOTIFICATION_TYPES) {
        channels[t.key] = { ...(channels[t.key] ?? { ...DEFAULT_PREFS }), [ch]: value };
      }
      return { ...prev, channels };
    });
  }

  async function save(): Promise<void> {
    if (!prefs) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateNotificationPreferences(prefs);
      setPrefs(updated);
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  const groups = [...new Set(NOTIFICATION_TYPES.map((t) => t.group))];

  return (
    <PageContainer>
      <PageHeader
        title="Notification preferences"
        subtitle="Choose which notification types pop in-app, send email, push to your devices, or play a sound. Quiet hours suppress everything except critical alerts."
        badges={[
          { label: 'Per-user', variant: 'glass' },
          ...(savedAt ? [{ label: 'Saved', variant: 'success' as const }] : []),
        ]}
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={saving || !prefs} data-action="disc.adm_notif_prefs.a01">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              {saving ? 'Saving…' : 'Save preferences'}
            </Button>
          </div>
        }
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error}</p>
        </GlassCard>
      )}

      {!prefs && !loading && !error && (
        <EmptyStateV2
          icon={Bell}
          title="No preferences on file"
          description="Defaults will apply: in-app notifications enabled for all types, sound off, push off, email off."
        />
      )}

      {prefs && (
        <>
          {/* Channel matrix */}
          <GlassCard className="p-0 overflow-hidden mb-6">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <div className="col-span-5">Notification type</div>
              {CHANNELS.map((c) => (
                <div key={c.key} className="col-span-1 md:col-span-1 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <c.icon className="h-3 w-3" />
                    <span>{c.label}</span>
                  </div>
                </div>
              ))}
              <div className="col-span-3 text-right text-[10px] text-muted-foreground/70">
                Click headers to set all
              </div>
            </div>
            {/* "All" row */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border/40 bg-muted/20 text-xs">
              <div className="col-span-5 text-muted-foreground italic">- Set all types -</div>
              {CHANNELS.map((c) => (
                <div key={c.key} className="col-span-1 text-center flex justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => setColumnAll(c.key, true)}
                    className="text-[9px] text-success hover:underline"
                    title={`Enable ${c.label} for all`}
                  >
                    on
                  </button>
                  <span className="text-muted-foreground/40">/</span>
                  <button
                    type="button"
                    onClick={() => setColumnAll(c.key, false)}
                    className="text-[9px] text-destructive hover:underline"
                    title={`Disable ${c.label} for all`}
                  >
                    off
                  </button>
                </div>
              ))}
              <div className="col-span-3" />
            </div>
            {groups.map((group) => (
              <div key={group}>
                <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/10 border-b border-border/30">
                  {group}
                </div>
                {NOTIFICATION_TYPES.filter((t) => t.group === group).map((t, i) => (
                  <motion.div
                    key={t.key}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.015 }}
                    className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border/30 items-center hover:bg-muted/10 transition-colors text-xs"
                  >
                    <div className="col-span-5 truncate"><Rx>{t.label}</Rx></div>
                    {CHANNELS.map((c) => (
                      <div key={c.key} className="col-span-1 text-center">
                        <input
                          type="checkbox"
                          checked={getChannel(t.key, c.key)}
                          onChange={() => toggle(t.key, c.key)}
                          className="h-3.5 w-3.5 cursor-pointer accent-primary"
                          aria-label={tx(`${t.label} - ${c.label}`)}
                        />
                      </div>
                    ))}
                    <div className="col-span-3 text-right">
                      <code className="text-[9px] text-muted-foreground/70 font-mono">{t.key}</code>
                    </div>
                  </motion.div>
                ))}
              </div>
            ))}
          </GlassCard>

          {/* Quiet hours + global toggles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <GlassCard className="p-5 md:col-span-2">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Moon className="h-4 w-4 text-primary" /> Quiet hours
                </CardTitle>
                <CardDescription>
                  Suppress non-critical notifications during these hours (your local time). Critical
                  + breach-clock-expired always fire.
                </CardDescription>
              </CardHeader>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase text-muted-foreground">Start</span>
                  <input
                    type="time"
                    value={prefs.quietHoursStart ?? ''}
                    onChange={(e) =>
                      setPrefs({ ...prefs, quietHoursStart: e.target.value || null })
                    }
                    className="glass-lite px-3 py-1.5 rounded-md border border-border/40 focus:outline-none focus:border-primary"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase text-muted-foreground">End</span>
                  <input
                    type="time"
                    value={prefs.quietHoursEnd ?? ''}
                    onChange={(e) => setPrefs({ ...prefs, quietHoursEnd: e.target.value || null })}
                    className="glass-lite px-3 py-1.5 rounded-md border border-border/40 focus:outline-none focus:border-primary"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase text-muted-foreground">Timezone</span>
                  <input
                    type="text"
                    placeholder="Asia/Kolkata"
                    value={prefs.quietHoursTz ?? ''}
                    onChange={(e) => setPrefs({ ...prefs, quietHoursTz: e.target.value })}
                    className="glass-lite px-3 py-1.5 rounded-md border border-border/40 focus:outline-none focus:border-primary"
                  />
                </label>
              </div>
            </GlassCard>

            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="text-sm">Global toggles</CardTitle>
                <CardDescription clamp>Master switches that override channel matrix.</CardDescription>
              </CardHeader>
              <div className="space-y-2 text-xs">
                <label className={cn('flex items-center justify-between glass-lite p-2 rounded-md cursor-pointer')}>
                  <span className="flex items-center gap-2">
                    <Volume2 className="h-3.5 w-3.5" />
                    Sound effects
                  </span>
                  <input
                    type="checkbox"
                    checked={prefs.soundEnabled ?? false}
                    onChange={(e) => setPrefs({ ...prefs, soundEnabled: e.target.checked })}
                    className="h-4 w-4 accent-primary"
                  />
                </label>
                <label
                  className={cn(
                    'flex items-center justify-between glass-lite p-2 rounded-md',
                    webPush.state === 'unsupported' ? 'opacity-50' : 'cursor-pointer',
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Smartphone className="h-3.5 w-3.5" />
                    Web Push
                    {webPush.busy && <Loader2 className="h-3 w-3 animate-spin" />}
                  </span>
                  <input
                    type="checkbox"
                    checked={webPush.state === 'granted-on'}
                    disabled={webPush.busy || webPush.state === 'unsupported' || webPush.state === 'denied'}
                    onChange={async (e) => {
                      const next = e.target.checked
                        ? await webPush.optIn()
                        : await webPush.optOut();
                      setPrefs({ ...prefs, pushEnabled: next === 'granted-on' });
                    }}
                    className="h-4 w-4 accent-primary"
                  />
                </label>
                {webPush.state === 'denied' && (
                  <div className="text-[10px] text-warning">
                    Notifications are blocked at the browser level. Re-enable them in your site
                    settings to use Web Push.
                  </div>
                )}
                {webPush.state === 'unsupported' && (
                  <div className="text-[10px] text-muted-foreground">
                    Web Push isn't supported in this browser.
                  </div>
                )}
                {webPush.error && (
                  <div className="text-[10px] text-destructive">Push: {webPush.error}</div>
                )}
                {prefs.pushSubscription && (
                  <div className="glass-lite rounded-md p-2 text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-success" />
                    Push subscription registered
                  </div>
                )}
              </div>
            </GlassCard>
          </div>

          {savedAt && (
            <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-success" />
              Saved at {new Date(savedAt).toLocaleString()}
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
